import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "validation"
    }}>{`Validation`}</h1>
    <p>{`Reforml provides way to utilize `}<a parentName="p" {...{
        "href": "//wiki.developer.mozilla.org/en-US/docs/Web/Guide/HTML/HTML5/Constraint_validation"
      }}>{`HTML constraints`}</a>{` and any custom validators including validators from other libraries.
You can also customize the error message`}</p>
    <h2 {...{
      "id": "html-constraints"
    }}>{`HTML Constraints`}</h2>
    <p>{`You can directly supply `}<a parentName="p" {...{
        "href": "//wiki.developer.mozilla.org/en-US/docs/Web/Guide/HTML/HTML5/Constraint_validation"
      }}>{`HTML constraints`}</a>{` in field definition, if you are using controlled form,
you need to manually called `}<inlineCode parentName="p">{`validate`}</inlineCode>{` in `}<inlineCode parentName="p">{`onChange`}</inlineCode>{` handler since HTML constraints are only validated in uncontrolled form`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`() => {
  const [value, setValue] = useState({})
  const [hasError, setHasError] = useState(false)
  const handleChange = (newValue, {validate}) => {
    const error = validate()
    setHasError(!!error)
    setValue(newValue)
  }
  const fields = useMemo(() => jsyaml.load(\`
  myText:
    type: text
    label: Input some value
    helperText: a required field with minLength
    required: true
    minLength: 6
  myNumber:
    type: number
    label: Number
    required: true
    min: 0
    max: 10
    step: 2
  \`), [])
  return (
    <div>
      <BaseForm fields={fields} value={value} onChange={handleChange}/>
      value:
      <pre>{JSON.stringify(value, null, 2)}</pre>
      <p>{hasError ? 'ERROR' : 'OK'}</p>
    </div>
  )
}
`}</code></pre>
    <h2 {...{
      "id": "custom-validation"
    }}>{`Custom Validation`}</h2>
    <p>{`By supplying a validatorDictionary to ReformlProvider,
BasicForm inside it can use yaml with field containing a validation property.
The validation property is a list of "validation rule name" or "rule name to params array map" or "rule name to single param"
You can notice the mapping of params in YAML definition to validator function implementation`}</p>
    <p>{`validatorDictionary is a rule name to validator map,
a validator is a function that accept first parameter as the field value,
rest parameter from field validation settings from the yaml,
and return a boolean to determine whether the value passes the validation.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`() => {
  const [value, setValue] = useState({})
  const [hasError, setHasError] = useState(false)
  const handleChange = (newValue, {validate}) => {
    const error = validate()
    setHasError(!!error)
    setValue(newValue)
  }
  const fields = useMemo(() => jsyaml.load(\`
  myText:
    type: text
    label: Must be Foo
    required: true
    validate:
      - isFoo
      - isEqual: Foo
      - isLengthBetween:
        - 2
        - 4
  \`), [])
  return (
    <ReformlProvider validatorDictionary={{
    isFoo: (value) => value === 'Foo',
    isEqual: (value, compare) => value === compare,
    isLengthBetween: (value, a, b) => value.length >= a && value.length <= b
    }}>
      <BaseForm fields={fields} value={value} onChange={handleChange}/>
      value:
      <pre>{JSON.stringify(value, null, 2)}</pre>
      <p>{hasError ? 'ERROR' : 'OK'}</p>
    </ReformlProvider>
  )
}
`}</code></pre>
    <p>{`In field definition you can even implement a validator function directly`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`() => {
  const [value, setValue] = useState({})
  const [hasError, setHasError] = useState(false)
  const handleChange = (newValue, {validate}) => {
    const error = validate()
    setHasError(!!error)
    setValue(newValue)
  }
  const fields = useMemo(() => {
  const fields = jsyaml.load(\`
  myText:
    type: text
    label: Must be Foo
    required: true
  \`)
  fields.myText.validate = [{
    isFoo: (value) => value === 'Foo'
  }]
  return fields
  }, [])
  return (
    <div>
      <BaseForm fields={fields} value={value} onChange={handleChange}/>
      value:
      <pre>{JSON.stringify(value, null, 2)}</pre>
      <p>{hasError ? 'ERROR' : 'OK'}</p>
    </div>
  )
}
`}</code></pre>
    <h2 {...{
      "id": "validatorjs-integration"
    }}>{`validator.js integration`}</h2>
    <p>{`The Validators used in reforml shares the same interface as validator.js, so you can use them directly`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm i validator
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import validators from 'validator'

const myValidators = {
  isFoo: (value) => value === 'Foo',
  isEqual: (value, compare) => value === compare,
  isLengthBetween: (value, a, b) => value.length >= a && value.length <= b
}

() => {
return <ReformlProvider validatorDictionary={{
  ...validators,
  ...myValidators
  }}>
    {/* BaseForm here */}
  </Reforml>
}
`}</code></pre>
    <h2 {...{
      "id": "customize-the-error-message"
    }}>{`Customize the error message`}</h2>
    <p>{`ValidateErrorFormatter has the following signature`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ts"
      }}>{`export type ValidateErrorFormatter = <T>(value: T, ruleName: string, params: never[]) => string
`}</code></pre>
    <p>{`value is the value supplied in the form, ruleName is the name of validation, params is the array of param supply to the validator`}</p>
    <p>{`and you can supply the formatter to ReformlProvider`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const validateErrorFormatter = (_, ruleName, params) => {
  return ruleName + (params.length === 0 ? '' : ':' + params.join(','))
}
() => {
  return <ReformlProvider validateErrorFormatter={validateErrorFormatter}>
      {/* BaseForm here */}
    </ReformlProvider>
  }
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      